import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MediaJob } from "@arc/contracts";
import { mediaJobApi } from "@/api/client.js";
import { cable } from "./cable.js";
import type { AuthSession } from "@/auth/keycloak.js";

/**
 * The caller's background jobs, kept live (ADR-026 Phase 6).
 *
 * **HTTP for the snapshot, the socket for the change.** The list has to survive a reload — a cut can outlive the page
 * that started it, and "you must not need a refresh" cannot be answered by client-only state. So the query is the
 * authority and the push is the invalidation trigger.
 *
 * The socket carries the finished job itself, which is what lets a completion also refresh the *manual* it belongs to:
 * the cut file is already attached server-side, so the editor just needs to re-read the body it is showing.
 */
export function useMediaJobs(session: AuthSession | null): {
  jobs: MediaJob[];
  active: MediaJob[];
  /** The first load has not answered yet — distinct from "answered, and there is nothing" (diff-panel R14-F2). */
  isLoading: boolean;
  /** The read failed. The list is EMPTY in this case too, which is why the two must not be conflated. */
  isError: boolean;
  retry: () => void;
} {
  const qc = useQueryClient();
  // Memoised so the subscribe effect below does not resubscribe on every render.
  const key = useMemo(() => ["media-jobs", session?.realm, session?.sub], [session?.realm, session?.sub]);

  const query = useQuery({
    queryKey: key,
    enabled: session != null,
    queryFn: async () => {
      const res = await mediaJobApi.listMyMediaJobs();
      if (res.status !== 200) throw new Error(`GET /media/jobs returned ${res.status}`);
      return res.body.jobs;
    },
    // A slow poll as the SAFETY NET only: the push is the fast path, but a dropped socket must not leave a job spinning
    // forever in the UI. Deliberately lazy — this is a fallback, not the mechanism.
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (!session) return;
    const onJob = (job: { manualId?: number | null }): void => {
      void qc.invalidateQueries({ queryKey: key });
      // The job's manual is now different on the server (the cut is attached), so re-read the body the editor shows.
      // Without this the notification would say "done" while the step still displayed the old video — the exact
      // "needs a refresh" the feature exists to remove.
      if (job.manualId != null) void qc.invalidateQueries({ queryKey: ["manual-body", session.realm, session.sub, job.manualId] });
    };
    const s = cable();
    s.on("media:job", onJob);
    return () => {
      s.off("media:job", onJob);
    };
  }, [qc, session, key]);

  const jobs = query.data ?? [];
  // `data ?? []` collapses THREE different situations into one empty array: still loading, failed, and genuinely empty.
  // The panel's trigger is permanent now, so its empty state makes a CLAIM — "nothing has been cut in the last 7 days".
  // Asserting that while the request is in flight, or after a backend outage, reintroduces exactly the "is it empty or
  // is it broken?" doubt this rework existed to remove. So the caller gets the state, not just the array.
  return {
    jobs,
    active: jobs.filter((j) => j.status === "pending" || j.status === "enqueued"),
    isLoading: query.isPending && session != null,
    isError: query.isError,
    retry: () => void query.refetch(),
  };
}
